// MapComponent.js
import React, { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Typography,
  Drawer,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Local component imports (same directory)
import { StatBox } from "./StatBox";
import { CostChart } from "./CostChart";
import { FilterControls } from "./FilterControls";
import { MapControls } from "./MapControls";
import { MenuDrawer } from "./MenuDrawer";
import { VendorSummaryBox } from "./VendorSummaryBox";
import { TechnologySelector } from "./TechnologySelector";
import { SiteTypeSelector } from "./SiteTypeSelector";
import PartnerSelector from "./PartnerSelector";
import { CostAnalysis } from "./CostAnalysis";
import { LocationMarkers } from "./LocationMarkers";
import {
  constants,
  VENDOR_COSTS,
  VENDORS,
  VENDOR_COLORS,
  mapTiles,
  defaultCenter,
  defaultZoom,
} from "./constants";
import { createCustomIcon, formatCurrency, getBusinessUnit } from "./helpers";
import { MKSLocationData } from "../Data/MKSLocationsV2";
import partners from "../Data/PartnerTypesV1";
import MKSLogoSmall from "../Graphics/MKSLogoSmall.png";

// Add to imports in MapComponentV2.js
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { useInsightsGeneration, InsightsDialog } from "./apiServices";
import infrastructureSetupCosts from "../Data/infrastructureSetupCosts";
import transitionCosts from "../Data/transitionCosts";
import insourced from "../Data/InSourced";
import outsourcedCosts from "../Data/OutSourced";
import TechnologyComparison from "./TechnologyComparison";
import LocationCircuits from "./LocationCircuits";
import TechnologyAnalysis from "./TechnologyAnalysis";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows"; // Using this instead of CompareIcon
import { VendorRanking } from "./VendorRanking";

import {
  calculateCosts,
  calculateSiteTypeCosts,
  calculatePartnerCosts,
} from "./calculations";
// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const MapComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [technologyAnalysisOpen, setTechnologyAnalysisOpen] = useState(false);

  // State declarations
  const [comparisonAnalysis, setComparisonAnalysis] = useState("");
  const [selectedCoManagePartners, setSelectedCoManagePartners] = useState([]);
  const [selectedOutsourcePartners, setSelectedOutsourcePartners] = useState(
    []
  );
  const [selectedHybridPartners, setSelectedHybridPartners] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locations] = useState(MKSLocationData.locations);
  const [filteredLocations, setFilteredLocations] = useState(
    MKSLocationData.locations
  );
  const [selectedMapType, setSelectedMapType] = useState("standard");
  const [showStats, setShowStats] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState(VENDORS);
  const [selectedSiteTypes, setSelectedSiteTypes] = useState([
    "Small",
    "Medium",
    "Large",
    "DC",
  ]);
  const [filters, setFilters] = useState({
    continent: null,
    country: null,
    businessName: null,
    businessUnit: null,
  });

  // Add inside MapComponent function, after other state declarations
  const [selectedLocation, setSelectedLocation] = useState(null);
  useEffect(() => {
    console.log("Selected Location:", selectedLocation);
  }, [selectedLocation]);
  const [insightsDialogOpen, setInsightsDialogOpen] = useState(false);
  const { analysis, loading, generateInsights } = useInsightsGeneration();
  // Add the handler function for insights click
  const handleInsightsClick = async () => {
    console.log("Insights Clicked");
    const selectedData = {
      filteredLocations,
      selectedVendors,
      selectedSiteTypes,
      costs,
      partnerCosts,
      infrastructureSetupCosts,
      transitionCosts,
      insourcedData: insourced,
      outsourcedData: outsourcedCosts,
    };

    setInsightsDialogOpen(true);
    await generateInsights(selectedData);
  };

  // Memoized values
  const dynamicFilterOptions = useMemo(
    () => ({
      continents: [...new Set(locations.map((loc) => loc.continent))].sort(),
      countries: [...new Set(locations.map((loc) => loc.country))].sort(),
      businessNames: [
        ...new Set(locations.map((loc) => loc.businessName)),
      ].sort(),
    }),
    [locations]
  );

  const totalSitesValue = useMemo(() => {
    return filteredLocations.filter((loc) =>
      selectedSiteTypes.includes(loc.size)
    ).length;
  }, [filteredLocations, selectedSiteTypes]);

  const costs = useMemo(
    () => calculateCosts(filteredLocations, selectedSiteTypes, selectedVendors),
    [filteredLocations, selectedSiteTypes, selectedVendors]
  );

const vendorTotals = useMemo(() => {
  const totals = {};
  selectedVendors.forEach((vendor) => {
    totals[vendor] = filteredLocations
      .filter((loc) => selectedSiteTypes.includes(loc.size))
      .reduce(
        (sum, location) => sum + VENDOR_COSTS[vendor][location.size],
        0
      );
  });
  return totals;
}, [filteredLocations, selectedSiteTypes, selectedVendors]);

const vendorData = useMemo(() => {
  return selectedVendors.map((vendor) => {
    const price = vendorTotals[vendor] || 0; // Now vendorTotals is defined before being used
    return { name: vendor, price };
  });
}, [selectedVendors, vendorTotals]);


  const siteTypeCosts = useMemo(
    () =>
      selectedSiteTypes.reduce(
        (acc, siteType) => ({
          ...acc,
          [siteType]: calculateSiteTypeCosts(
            filteredLocations,
            siteType,
            selectedVendors
          ),
        }),
        {}
      ),
    [filteredLocations, selectedSiteTypes, selectedVendors]
  );

  const partnerCosts = useMemo(
    () => ({
      coManage: calculatePartnerCosts(
        filteredLocations,
        selectedCoManagePartners,
        "coManage",
        selectedSiteTypes
      ),
      outsource: calculatePartnerCosts(
        filteredLocations,
        selectedOutsourcePartners,
        "outsource",
        selectedSiteTypes
      ),
      hybrid: calculatePartnerCosts(
        filteredLocations,
        selectedHybridPartners,
        "hybrid",
        selectedSiteTypes
      ),
    }),
    [
      filteredLocations,
      selectedCoManagePartners,
      selectedOutsourcePartners,
      selectedHybridPartners,
      selectedSiteTypes,
    ]
  );

  // Event handlers
  const handleMenuToggle = () => setMenuOpen(!menuOpen);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleVendorChange = (event) => {
    const value = event.target.value;
    setSelectedVendors(typeof value === "string" ? value.split(",") : value);
  };

  const handleSiteTypeChange = (event) => {
    const value = event.target.value;
    setSelectedSiteTypes(typeof value === "string" ? value.split(",") : value);
  };

  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleCoManageChange = (event) => {
    const value = event.target.value;
    if (!value.includes("NA")) {
      setSelectedCoManagePartners(value);
    }
  };

  const handleOutsourceChange = (event) => {
    const value = event.target.value;
    if (!value.includes("NA")) {
      setSelectedOutsourcePartners(value);
    }
  };

  const handleHybridChange = (event) => {
    const value = event.target.value;
    if (!value.includes("NA")) {
      setSelectedHybridPartners(value);
    }
  };

  // Filter effect
  useEffect(() => {
    let filtered = [...locations];

    if (filters.businessUnit) {
      filtered = filtered.filter(
        (loc) => getBusinessUnit(loc.abbreviation) === filters.businessUnit
      );
    }
    if (filters.continent) {
      filtered = filtered.filter((loc) => loc.continent === filters.continent);
    }
    if (filters.country) {
      filtered = filtered.filter((loc) => loc.country === filters.country);
    }
    if (filters.businessName) {
      filtered = filtered.filter(
        (loc) => loc.businessName === filters.businessName
      );
    }

    filtered = filtered.filter((loc) => selectedSiteTypes.includes(loc.size));

    setFilteredLocations(filtered);
  }, [filters, locations, selectedSiteTypes]);

  // MapUpdater component
  const MapUpdater = ({ locations, filters }) => {
    const map = useMap();

    useEffect(() => {
      if (locations.length === 0) {
        map.setView(defaultCenter, defaultZoom);
        return;
      }

      if (locations.length === 1) {
        map.setView([locations[0].position.lat, locations[0].position.lng], 13);
        return;
      }

      const bounds = L.latLngBounds(
        locations.map((loc) => [loc.position.lat, loc.position.lng])
      );

      map.fitBounds(bounds, {
        padding: [30, 30],
        maxZoom: 10,
        animate: true,
        duration: 0.5,
      });

      setTimeout(() => {
        if (!map.getBounds().contains(bounds)) {
          map.fitBounds(bounds, {
            padding: [30, 30],
            animate: true,
          });
        }
      }, 100);
    }, [map, locations, filters]);

    return null;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <AppBar
        position="static"
        sx={{ backgroundColor: "white", color: "black" }}
      >
        <Toolbar>
          <Box
            sx={{
              height: "48px",
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              justifyContent: "center",
            }}
          >
            <img
              src={MKSLogoSmall}
              alt="MKS Logo"
              style={{ maxHeight: "100%", width: "auto", objectFit: "contain" }}
            />
          </Box>
          <IconButton color="inherit" onClick={handleMenuToggle}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, display: "flex", height: "calc(100vh - 64px)" }}>
        {showStats && (
          <Box
            sx={{
              width: "19%",
              minWidth: "200px",
              mr: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              overflow: "auto",
            }}
          >
            <StatBox
              title="Total Sites"
              value={totalSitesValue}
              subtitle="Based on current filters and selected site types"
            />
            <VendorRanking
              vendors={vendorData}
            />

            <Paper elevation={3} sx={{ p: 1.5 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontSize: "0.9rem" }}
              >
                Vendor Summary
              </Typography>
              {[...selectedVendors]
                .sort((a, b) => vendorTotals[a] - vendorTotals[b]) // Sort vendors by their totals
                .map((vendor) => (
                  <VendorSummaryBox
                    key={vendor}
                    vendor={vendor}
                    total={vendorTotals[vendor]}
                    color={VENDOR_COLORS[vendor]}
                  />
                ))}
            </Paper>

            <PartnerSelector
              title="Co-Manage Partner"
              partnerType="coManage"
              selected={selectedCoManagePartners}
              onChange={handleCoManageChange}
              partners={partners}
              filteredLocations={filteredLocations}
              selectedSiteTypes={selectedSiteTypes}
            />

            <PartnerSelector
              title="Outsource Partner"
              partnerType="outsource"
              selected={selectedOutsourcePartners}
              onChange={handleOutsourceChange}
            />

            <PartnerSelector
              title="Hybrid Partner"
              partnerType="hybrid"
              selected={selectedHybridPartners}
              onChange={handleHybridChange}
            />

            <TechnologySelector
              selectedVendors={selectedVendors}
              onChange={handleVendorChange}
            />

            <SiteTypeSelector
              selectedSiteTypes={selectedSiteTypes}
              onChange={handleSiteTypeChange}
              filteredLocations={filteredLocations}
            />

            <CostAnalysis
              costs={costs}
              selectedVendors={selectedVendors}
              siteTypeCosts={siteTypeCosts}
              selectedSiteTypes={selectedSiteTypes}
              filteredLocations={filteredLocations}
              partnerCosts={partnerCosts}
            />

            <Box sx={{ mt: 2 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setTechnologyAnalysisOpen(true)}
              >
                <CompareArrowsIcon sx={{ mr: 1 }} />
                <Typography>Compare Technologies</Typography>
              </Paper>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={handleInsightsClick}
              >
                <LightbulbIcon sx={{ mr: 1 }} />
                <Typography>Generate Infrastructure Insights</Typography>
              </Paper>
            </Box>

            <TechnologyComparison
              onAnalysisComplete={(analysis) => {
                setComparisonAnalysis(analysis); // Use the new state setter
                setInsightsDialogOpen(true);
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
          }}
        >
          <FilterControls
            showFilters={showFilters}
            filters={filters}
            handleFilterChange={handleFilterChange}
            locations={locations} // Add this prop
            isMobile={isMobile}
            selectedSiteTypes={selectedSiteTypes}
          />

          <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
            <MapControls
              showControls={showControls}
              selectedMapType={selectedMapType}
              setSelectedMapType={setSelectedMapType}
            />

            <MapContainer
              center={defaultCenter}
              zoom={defaultZoom}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={true}
            >
              <MapUpdater locations={filteredLocations} filters={filters} />
              <TileLayer
                attribution={mapTiles[selectedMapType].attribution}
                url={mapTiles[selectedMapType].url}
              />
              <LocationMarkers
                filteredLocations={filteredLocations}
                getBusinessUnit={getBusinessUnit}
                selectedVendors={selectedVendors}
                VENDOR_COSTS={VENDOR_COSTS}
                onLocationSelect={(location) => {
                  console.log("Location selected:", location);
                  setSelectedLocation(location);
                }}
              />
            </MapContainer>
            {selectedLocation && (
              <Box
                sx={{
                  position: "fixed", // Changed from absolute to fixed
                  bottom: 0,
                  left: 0,
                  right: 0,
                  maxHeight: "60vh",
                  overflow: "auto",
                  zIndex: 2000, // Increased z-index
                  m: 2, // Added margin
                  pointerEvents: "auto", // Ensure clicks work
                }}
              >
                <LocationCircuits
                  selectedLocation={selectedLocation}
                  onClose={() => setSelectedLocation(null)}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <MenuDrawer
        menuOpen={menuOpen}
        handleMenuToggle={handleMenuToggle}
        showStats={showStats}
        setShowStats={setShowStats}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        showControls={showControls}
        setShowControls={setShowControls}
      />

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { width: 280, boxSizing: "border-box" },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
        <FilterControls
          showFilters={showFilters}
          filters={filters}
          handleFilterChange={handleFilterChange}
          dynamicOptions={dynamicFilterOptions}
          isMobile={isMobile}
          selectedSiteTypes={selectedSiteTypes}
        />
      </Drawer>

      <Dialog
        open={technologyAnalysisOpen}
        onClose={() => setTechnologyAnalysisOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            width: "90%",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Technology Comparison</Typography>
            <IconButton onClick={() => setTechnologyAnalysisOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TechnologyAnalysis selectedVendors={selectedVendors} />
        </DialogContent>
      </Dialog>

      <InsightsDialog
        open={insightsDialogOpen}
        handleClose={() => setInsightsDialogOpen(false)}
        analysis={analysis || comparisonAnalysis} // Use whichever analysis is available
        loading={loading}
      />
    </Box>
  );
};

export default MapComponent;
